import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateAvatar, updateProfile } from "../../../api/profile";
import { ENUMLANGUAGE_OPTION } from "../../../generated/enums";
import { AccountWithoutPasswordAndCompany } from "../../../generated/types";
import {
  accountLanguageToLanguage,
  applyLanguage,
} from "../../../i18n/userLanguage";
import { isAssistant, SPACE_META, spaceForType } from "../../../utils/roles";
import { Avatar } from "../../common/Avatar";
import { Button } from "../../common/Button";
import { CityField } from "../../common/CityField";
import { FormInput, FormSelect } from "../../common/FormLayout";
import { sectionToIcon } from "../../type/default";
import { AvatarPickerModal } from "./AvatarPickerModal";
import { ProfileCard } from "./ProfileCard";

const AlertMessages = ({
  error,
  success,
}: {
  error: string | null;
  success: string | null;
}) => {
  if (!error && !success) return null;

  return (
    <div role="status" aria-live="polite" className="space-y-3">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="p-4 bg-secondary bg-opacity-20 border border-secondary text-tertiary rounded-lg">
          {success}
        </div>
      )}
    </div>
  );
};

export const ProfileAccountTab = ({
  profileData,
  onProfileUpdate,
}: {
  profileData: AccountWithoutPasswordAndCompany;
  onProfileUpdate: (updatedData: AccountWithoutPasswordAndCompany) => void;
}) => {
  const { t } = useTranslation();
  const [editedProfile, setEditedProfile] = useState(profileData);
  const [isAvatarEditorOpen, setIsAvatarEditorOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Raison sociale et SIRET engagent juridiquement et sont rapproches de l'ERP (X3) : ils
  // ne se corrigent pas ici mais via le support, qui tranche le cas.

  // Un collaborateur consulte l'entreprise sans la modifier : elle est celle de son
  // responsable. Garde-fou d'affichage — c'est le backend qui refuse l'écriture.
  const canEditCompany = !isAssistant(profileData.account.type);

  /**
   * « Modifie » = les valeurs DIFFERENT de celles chargees, et non « l'utilisateur a
   * touche un champ ». L'ancien `isEditing` passait a true au premier evenement et ne
   * redescendait jamais : retaper la meme valeur, ou simplement focaliser un champ
   * desactive, affichait la barre d'enregistrement pour rien.
   *
   * La comparaison JSON suffit : l'objet est petit, plat, et vient d'un meme fetch —
   * l'ordre des cles est donc stable.
   */
  const isDirty = useMemo(
    () => JSON.stringify(editedProfile) !== JSON.stringify(profileData),
    [editedProfile, profileData]
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name.startsWith("company_")) {
      const companyField = name.replace("company_", "");
      setEditedProfile((prev: AccountWithoutPasswordAndCompany) => ({
        ...prev,
        company: {
          ...prev.company,
          [companyField]: value,
          // Changer de code postal invalide la ville choisie pour l'ancien.
          ...(companyField === "zip_code" && { city: "" }),
        },
      }));
    } else {
      setEditedProfile((prev: AccountWithoutPasswordAndCompany) => ({
        ...prev,
        account: { ...prev.account, [name]: value },
      }));
    }
  };

  const handleCityChange = (city: string, zipCode: string) => {
    setEditedProfile((prev: AccountWithoutPasswordAndCompany) => ({
      ...prev,
      company: { ...prev.company, city, zip_code: zipCode },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsSaving(true);
    try {
      const result = await updateProfile(editedProfile);
      if (result.success && result.data) {
        applyLanguage(accountLanguageToLanguage[result.data.account.language]);
        onProfileUpdate({
          account: result.data.account,
          company: result.data.company,
        } as AccountWithoutPasswordAndCompany);
        setSuccess(t("profile-update-success"));
      } else if (result.error) {
        setError(result.error);
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour du profil:", error);
      setError(t("profile-update-error"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedProfile(profileData);
    setError(null);
    setSuccess(null);
  };

  const handleAvatarSave = async (avatarId: string) => {
    try {
      await updateAvatar(avatarId, profileData.account);
      // Construire l'objet mis à jour et l'utiliser pour l'état ET onProfileUpdate,
      // pour éviter la closure périmée (setEditedProfile est asynchrone).
      const updated: AccountWithoutPasswordAndCompany = {
        ...editedProfile,
        account: { ...editedProfile.account, avatar: avatarId },
      };
      setEditedProfile(updated);
      onProfileUpdate(updated);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde de l'avatar:", error);
    }
  };

  // Rendus a deux endroits selon la largeur (colonne laterale ou fin du formulaire).
  const saveButtons = (
    <>
      <Button
        type="submit"
        disabled={isSaving}
        size="small"
        className={`w-44 py-2 whitespace-nowrap shadow-md ${
          isSaving ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        {isSaving ? t("profile-saving") : t("button-save")}
      </Button>
      <Button
        type="button"
        variant="secondary"
        onClick={handleCancel}
        disabled={isSaving}
        size="small"
        className="w-44 py-2 whitespace-nowrap shadow-md"
      >
        {t("button-cancel")}
      </Button>
    </>
  );

  const account = editedProfile.account;
  const company = editedProfile.company;
  const space = spaceForType(account.type);

  return (
    <>
      <form onSubmit={handleSubmit}>
        {/* Identite a gauche, formulaire a cote a partir de `lg` ; en dessous tout s'empile,
            l'identite en premier. La colonne laterale reste visible au defilement et porte
            les boutons d'enregistrement : ils ne recouvrent jamais le formulaire. */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-center gap-4">
          <aside className="w-full lg:w-72 shrink-0 space-y-4 lg:sticky lg:top-4">
            <div className="bg-white rounded-lg shadow-md p-4 md:p-6 flex flex-col items-center">
              <button
                id="tour-profile-avatar"
                type="button"
                onClick={() => setIsAvatarEditorOpen(true)}
                aria-label={t("profile-avatar-edit")}
                title={t("profile-avatar-edit")}
                className="group relative w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden ring-2 ring-grey-light hover:ring-secondary focus:outline-none focus:ring-4 focus:ring-secondary transition"
              >
                <Avatar
                  value={account.avatar}
                  firstName={account.first_name}
                  lastName={account.last_name}
                  emojiClassName="text-5xl"
                  initialsClassName="text-3xl"
                />
                <span className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition flex items-center justify-center text-white text-sm font-oxaniumMedium">
                  {t("profile-avatar-edit-short")}
                </span>
              </button>

              <p className="mt-4 font-oxaniumMedium text-lg text-tertiary text-center leading-tight">
                {account.first_name} {account.last_name}
              </p>
              <p className="text-sm text-grey-readable text-center break-all mt-1">
                {account.email}
              </p>

              <div className="w-full mt-4 pt-4 border-t border-grey-light">
                {/* Role + entreprise : hierarchie TYPOGRAPHIQUE, pas deux pilules.
                  Deux pilules donnaient le meme poids visuel a deux informations de
                  nature differente (un statut, un rattachement) et le nom d'entreprise
                  long faisait deborder la pastille.

                  Le libelle et la couleur viennent de `SPACE_META` : « admin » brut
                  devient « Admin », et chaque espace a deja son accent (gris / vert /
                  orange / violet). La couleur ne porte QUE le lisere — jamais le texte,
                  qui resterait sous le seuil de contraste AA sur du blanc. */}
                <div
                  className="flex items-center justify-center gap-2 border-l-[3px] pl-3 mx-auto w-fit max-w-full"
                  style={{ borderColor: SPACE_META[space].accent }}
                >
                  <span className="text-sm font-oxaniumMedium text-tertiary whitespace-nowrap">
                    {SPACE_META[space].label}
                  </span>
                  {company.company_name && (
                    <>
                      <span aria-hidden="true" className="text-grey-medium">
                        ·
                      </span>
                      {/* `min-w-0` est indispensable : un enfant flex a `min-width:auto`
                        par defaut et REFUSE de se reduire sous la largeur de son texte,
                        donc `truncate` seul ne coupe rien et le nom deborde la carte. */}
                      <span
                        className="text-sm text-grey-readable truncate min-w-0"
                        title={company.company_name}
                      >
                        {company.company_name}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {isDirty && (
              <div className="hidden lg:flex flex-col items-center gap-3">
                {saveButtons}
              </div>
            )}
          </aside>

          <div className="w-full max-w-3xl min-w-0 space-y-4">
            <div id="tour-profile-form" className="space-y-4">
              <AlertMessages error={error} success={success} />

              <ProfileCard
                title={t("profile-info-title")}
                icon={sectionToIcon.section_profile}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    label={t("account-last-name")}
                    name="last_name"
                    autoComplete="family-name"
                    value={account.last_name}
                    onChange={handleChange}
                    required
                  />
                  <FormInput
                    label={t("account-first-name")}
                    name="first_name"
                    autoComplete="given-name"
                    value={account.first_name}
                    onChange={handleChange}
                    required
                  />
                  <FormInput
                    label={t("account-email")}
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={account.email}
                    onChange={handleChange}
                    required
                  />
                  <FormInput
                    label={t("account-phone-mobile")}
                    type="tel"
                    name="phone_mobile"
                    autoComplete="tel"
                    value={account.phone_mobile}
                    onChange={handleChange}
                    required
                  />
                  <FormInput
                    label={t("account-phone-fixe")}
                    type="tel"
                    name="phone_fixe"
                    value={account.phone_fixe}
                    onChange={handleChange}
                    required
                  />
                  <FormSelect
                    label={t("account-language")}
                    name="language"
                    value={account.language}
                    options={ENUMLANGUAGE_OPTION}
                    onChange={handleChange}
                    required
                  />
                </div>
              </ProfileCard>

              <ProfileCard
                title={t("profile-company-title")}
                icon={sectionToIcon.section_company}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    label={t("company-name")}
                    name="company_name"
                    value={company.company_name}
                    lockedHint={t("profile-company-locked-hint")}
                    required
                  />
                  <FormInput
                    label={t("company-siret")}
                    name="company_siret"
                    value={company.siret}
                    lockedHint={t("profile-company-locked-hint")}
                    required
                  />
                  <FormInput
                    label={t("company-email")}
                    type="email"
                    name="company_email"
                    value={company.email}
                    onChange={handleChange}
                    disabled={!canEditCompany}
                    required
                  />
                  <FormInput
                    label={t("company-phone-mobile")}
                    type="tel"
                    name="company_phone_mobile"
                    value={company.phone_mobile}
                    onChange={handleChange}
                    disabled={!canEditCompany}
                    required
                  />
                  <FormInput
                    label={t("company-phone-fixe")}
                    type="tel"
                    name="company_phone_fixe"
                    value={company.phone_fixe}
                    onChange={handleChange}
                    disabled={!canEditCompany}
                    required
                  />
                </div>
              </ProfileCard>

              <ProfileCard
                title={t("profile-address-title")}
                icon={sectionToIcon.section_building}
              >
                <div className="space-y-4">
                  <FormInput
                    label={t("company-address")}
                    name="company_address"
                    value={company.address}
                    onChange={handleChange}
                    disabled={!canEditCompany}
                    required
                  />
                  <FormInput
                    label={t("company-address-complementary")}
                    name="company_address_complementary"
                    value={company.address_complementary}
                    onChange={handleChange}
                    disabled={!canEditCompany}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormInput
                      label={t("company-zip-code")}
                      name="company_zip_code"
                      inputMode="numeric"
                      maxLength={5}
                      value={company.zip_code}
                      onChange={handleChange}
                      disabled={!canEditCompany}
                      required
                    />

                    <CityField
                      label={t("company-city")}
                      zipCode={company.zip_code}
                      value={company.city}
                      onChange={handleCityChange}
                      disabled={!canEditCompany}
                      required
                    />

                    <FormInput
                      label={t("company-country")}
                      name="company_country"
                      value={t("profile-country-france")}
                      lockedHint={t("profile-country-locked-hint")}
                      required
                    />
                  </div>
                </div>
              </ProfileCard>
            </div>

            {isDirty && (
              <div className="flex flex-row-reverse justify-start gap-3 lg:hidden">
                {saveButtons}
              </div>
            )}
          </div>
        </div>
      </form>

      <AvatarPickerModal
        isOpen={isAvatarEditorOpen}
        onClose={() => setIsAvatarEditorOpen(false)}
        onSelect={handleAvatarSave}
        currentValue={profileData.account.avatar}
        space={spaceForType(profileData.account.type)}
      />
    </>
  );
};
